import { ANY_FIELD, expandField, isMask } from './address';
var ID = 'ui-czna-authorities.fieldQuery.transcript';
/**
 * Message ids, written out in full: the translation-integrity gate reads them as static
 * ids, so a missing translation fails the build instead of showing a raw key.
 */
export var TRANSCRIPT_IDS = {
    sentence: "".concat(ID, ".sentence"),
    words: "".concat(ID, ".words"),
    and: "".concat(ID, ".and"),
    or: "".concat(ID, ".or"),
    has: "".concat(ID, ".has"),
    hasNot: "".concat(ID, ".hasNot"),
    isType: "".concat(ID, ".isType"),
    isNotType: "".concat(ID, ".isNotType"),
    fieldTag: "".concat(ID, ".field.tag"),
    fieldTagLabel: "".concat(ID, ".field.tagLabel"),
    fieldSubfield: "".concat(ID, ".field.subfield"),
    fieldSubfieldLabel: "".concat(ID, ".field.subfieldLabel"),
    fieldMask: "".concat(ID, ".field.mask"),
    fieldMaskSubfield: "".concat(ID, ".field.maskSubfield"),
    fieldAny: "".concat(ID, ".field.any"),
    fieldAnySubfield: "".concat(ID, ".field.anySubfield"),
    relationContains: "".concat(ID, ".relation.contains"),
    relationStartsWith: "".concat(ID, ".relation.startsWith"),
    relationEquals: "".concat(ID, ".relation.equals"),
    relationMatches: "".concat(ID, ".relation.matches"),
    relationExists: "".concat(ID, ".relation.exists"),
    noteIgnoresAccents: "".concat(ID, ".note.ignoresAccents"),
    noteKeepsAccents: "".concat(ID, ".note.keepsAccents"),
    noteAnywhere: "".concat(ID, ".note.anywhere"),
    noteMatchCase: "".concat(ID, ".note.matchCase"),
};
var RELATION_IDS = {
    CONTAINS: TRANSCRIPT_IDS.relationContains,
    STARTS_WITH: TRANSCRIPT_IDS.relationStartsWith,
    EQUALS: TRANSCRIPT_IDS.relationEquals,
    MATCHES: TRANSCRIPT_IDS.relationMatches,
    EXISTS: TRANSCRIPT_IDS.relationExists,
};
/** At most this many tags are spelled out for a mask; the rest is `…`. */
var MAX_LISTED_TAGS = 12;
var describeField = function (context, condition) {
    var _a, _b;
    var format = context.format, catalog = context.catalog;
    var field = condition.field, subfield = condition.subfield;
    if (field === ANY_FIELD) {
        return subfield
            ? format(TRANSCRIPT_IDS.fieldAnySubfield, { subfield: subfield })
            : format(TRANSCRIPT_IDS.fieldAny);
    }
    if (isMask(field)) {
        var tags = (_a = expandField(field, catalog)) !== null && _a !== void 0 ? _a : [];
        var listed = tags.length > MAX_LISTED_TAGS
            ? "".concat(tags.slice(0, MAX_LISTED_TAGS).join(', '), ", \u2026")
            : tags.join(', ');
        return subfield
            ? format(TRANSCRIPT_IDS.fieldMaskSubfield, { mask: field, tags: listed, subfield: subfield })
            : format(TRANSCRIPT_IDS.fieldMask, { mask: field, tags: listed });
    }
    var entry = catalog.fields.find(function (candidate) { return candidate.tag === field; });
    if (subfield) {
        var label = (_b = entry === null || entry === void 0 ? void 0 : entry.subfields.find(function (candidate) { return candidate.code === subfield; })) === null || _b === void 0 ? void 0 : _b.label;
        return label
            ? format(TRANSCRIPT_IDS.fieldSubfieldLabel, { tag: field, subfield: subfield, label: label })
            : format(TRANSCRIPT_IDS.fieldSubfield, { tag: field, subfield: subfield });
    }
    return (entry === null || entry === void 0 ? void 0 : entry.label)
        ? format(TRANSCRIPT_IDS.fieldTagLabel, { tag: field, label: entry.label })
        : format(TRANSCRIPT_IDS.fieldTag, { tag: field });
};
var describeLeaf = function (context, node, negated) {
    var _a, _b;
    var format = context.format;
    context.conditions += 1;
    if ('attribute' in node) {
        var type = (_a = context.typeLabels.get(node.value)) !== null && _a !== void 0 ? _a : node.value;
        return format(negated ? TRANSCRIPT_IDS.isNotType : TRANSCRIPT_IDS.isType, { type: type });
    }
    if (!('field' in node)) {
        return '';
    }
    context.relations.add(node.relation);
    if (node.matchCase) {
        context.matchCase = true;
    }
    var condition = format(RELATION_IDS[node.relation], {
        field: describeField(context, node),
        value: (_b = node.value) !== null && _b !== void 0 ? _b : '',
    });
    return format(negated ? TRANSCRIPT_IDS.hasNot : TRANSCRIPT_IDS.has, { condition: condition });
};
var describeNode = function (context, node, nested) {
    if ('op' in node) {
        if (node.op === 'not') {
            return describeLeaf(context, node.child, true);
        }
        var parts = node.children.map(function (child) { return describeNode(context, child, true); });
        var joined = parts.join(context.format(node.op === 'and' ? TRANSCRIPT_IDS.and : TRANSCRIPT_IDS.or));
        return nested ? "(".concat(joined, ")") : joined;
    }
    return describeLeaf(context, node, false);
};
/**
 * CZNA-217 — the Czech transcript under the query line: one sentence saying what will be
 * searched, built from the COMPILED query (what is sent), not from the typed text, plus
 * the notes on case, diacritics and "anywhere in the record" that apply to it.
 * Returns an empty list when there is nothing to search for.
 */
export var describeFieldQuery = function (compiled, options) {
    var context = {
        catalog: options.catalog,
        typeLabels: new Map(options.recordTypes.map(function (type) { return [type.code, type.label]; })),
        format: options.format,
        relations: new Set(),
        conditions: 0,
        matchCase: false,
    };
    var parts = [];
    if (compiled.words) {
        parts.push(options.format(TRANSCRIPT_IDS.words, { words: compiled.words }));
    }
    if (compiled.tree) {
        var tree = compiled.tree;
        // A top-level AND reads as a flat list joined to the words; any other root is one part.
        if ('op' in tree && tree.op === 'and') {
            parts.push.apply(parts, tree.children.map(function (child) { return describeNode(context, child, true); }));
        }
        else {
            // Next to the words an OR needs its parentheses, or "slova a A nebo B" would read
            // as "(slova a A) nebo B".
            parts.push(describeNode(context, tree, parts.length > 0));
        }
    }
    if (parts.length === 0) {
        return [];
    }
    var lines = [
        options.format(TRANSCRIPT_IDS.sentence, {
            conditions: parts.join(options.format(TRANSCRIPT_IDS.and)),
        }),
    ];
    // CZNA-1400 — the checkbox marks every valued condition, so the folding notes would
    // describe a comparison that no longer runs.
    if (context.matchCase) {
        lines.push(options.format(TRANSCRIPT_IDS.noteMatchCase));
    }
    else {
        if (context.relations.has('CONTAINS') || context.relations.has('STARTS_WITH')) {
            lines.push(options.format(TRANSCRIPT_IDS.noteIgnoresAccents));
        }
        if (context.relations.has('EQUALS') || context.relations.has('MATCHES')) {
            lines.push(options.format(TRANSCRIPT_IDS.noteKeepsAccents));
        }
    }
    if (context.conditions > 1) {
        lines.push(options.format(TRANSCRIPT_IDS.noteAnywhere));
    }
    return lines;
};
